import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CellEditingStoppedEvent, GridApi, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ShipmentListsModalComponent } from '../shipment-lists-modal/shipment-lists-modal.component';
import { ContactTypeEditorComponent } from 'src/app/contacts/contact-type-editor/contact-type-editor.component';
import { ModalService } from '../shipment-lists-modal/shipment-lists-modal.component';
import { finalize, Subscription } from 'rxjs';
import { NgZone } from '@angular/core';

@Component({
  selector: 'app-shipment-full-details',
  templateUrl: './shipment-full-details.component.html',
  styleUrls: ['./shipment-full-details.component.scss']
})
export class ShipmentFullDetailsComponent implements OnInit, OnDestroy {

  shipment_uuid!: string;
  shipment: any = {};
  pallet: any[] = [];
  gridApi!: GridApi;
  barcodes: any[] = [];
  dateOfScan: string = '';
  actualNumOfCartons: number = 0;
  receivedCartons: number = 0;
  isLoading = false;
  loadError = '';
  detailsCollapsed = false;
  palletsCollapsed = false;
  barcodesCollapsed = false;
  barcodeSearch = '';
  palletCount: number = 0;
  totalWeight: number = 0;
  alternate_reference: string = '';
  statuses: string[] = [
    "",
    "MANUAL AUDIT",
    "MISSING",
    "OVERAGE",
    "OPENED",
    "BROKEN",
    "CRUSHED",
    "WET",
    "HOLDOVER"
  ];
  barcodeColumnDefs: any[] = [];

  private subscriptions = new Subscription();
  private loadSubscription?: Subscription;

  constructor(private route: ActivatedRoute, private http: HttpClient, private dialog: MatDialog, private modalService: ModalService, private zone: NgZone) { }

  ngOnInit(): void {
    this.subscriptions.add(this.route.paramMap.subscribe(params => {
      this.shipment_uuid = params.get('uuid') || '';
      this.shipment = {};
      this.barcodeSearch = '';
      this.loadData();
    }));
    this.subscriptions.add(this.modalService.modalClosed$.subscribe(() => this.loadData()));
  }

  loadData() {
    this.loadSubscription?.unsubscribe();
    if (!this.shipment_uuid) {
      this.loadError = 'No shipment was selected.';
      return;
    }
    this.isLoading = true;
    this.loadError = '';
    this.loadSubscription = this.http.get('/api/shipment_lists/shipment_details.php', {
      params: { uuid: this.shipment_uuid }
    }).pipe(finalize(() => this.isLoading = false)).subscribe({
      next: (response: any) => {
        if (!response?.details?.shipment_list_uuid) {
          this.loadError = 'Shipment details could not be loaded. Please try again.';
          return;
        }
        this.shipment = response;
        this.barcodes = Array.isArray(response.barcode) ? response.barcode : [];
        this.pallet = Array.isArray(response.pallets) ? response.pallets : [];
        this.shipment.lading = Array.isArray(response.lading) ? response.lading : [];
        this.shipment.services = Array.isArray(response.services) ? response.services : [];
        this.alternate_reference = response.details.alternate_reference || '';
        this.barcodeColumnDefs = this.barcodeColumns();
        this.totalWeight = Number(response.details.reported_weight) || 0;
        this.dateOfScan = this.barcodes[0]?.intake_date || response.details.date || '';
        this.actualNumOfCartons = this.barcodes.filter((item: any) => this.isTrue(item.audited)).length;
        this.receivedCartons = this.barcodes.filter((item: any) => this.isTrue(item.received_in)).length;
        this.palletCount = this.pallet.reduce((count: number, item: any) =>
          count + (Number(item.cartons_pallet) || 0), 0
        );
      },
      error: () => this.loadError = 'Shipment details could not be loaded. Please try again.'
    });
  }

  isTrue(value: unknown): boolean {
    return value === true || value === 'true';
  }

  get missingCartons(): number {
    return this.barcodes.filter(item =>
      String(item.return_status || '').split(',').some(status => status.trim() === 'MISSING')
    ).length;
  }

  filterBarcodes(value: string): void {
    this.barcodeSearch = value;
    this.gridApi?.setGridOption('quickFilterText', value);
  }

  trackPallet(index: number, item: any): string | number {
    return item.package_uuid || index;
  }

  trackLeg(index: number, item: any): string | number {
    return item.lading_uuid || index;
  }

  barcodeColumns() { return [
    {
      headerName: 'No.', 
      pinned: 'left',
      filter: false,
      sortable: false,
      cellStyle: {'font-weight': 'bold'},
      valueGetter: (params: any) => {
        let couter = params.node.rowIndex + 1;
        // couter += 1;
        return couter;
      },
      width: 75,
    },
    { field: 'pallet_no', headerName: 'Pallet', width: 110,
      valueFormatter: function (params: ValueFormatterParams) {
        if (params.value == null || params.value === "NO Pallet") return '—';
        return params.value;
      }         
    },
    { field: 'barcode', headerName: 'Barcode', width: 250  },
    { field: 'received_in', 
      headerName: 'Received', 
      width: 125, 
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === true || params.value === 'true' ? 'Yes' : 'No';
      }    
    },
    { field: 'audited', 
      headerName: 'Audited', 
      width: 110, 
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === true || params.value === 'true' ? 'Yes' : 'No';
      }    
    },
    {
      field: 'delivered',
      headerName: 'Delivered',
      width: 120, 
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === true || params.value === 'true' ? 'Yes' : 'No';
      }
    }, 
    {
      field: 'delivered_method',
      headerName: 'Method',
      width: 150, 
    },         
    {
      field: 'delivered_time',
      headerName: 'Time',
      width: 150, 
    },       
    {
      field: 'return_status', 
      headerName: 'Status', 
      width: 150, 
      editable: true,
      cellRenderer: shipmentStatusFormatting,
      cellEditor: ContactTypeEditorComponent,
      cellEditorPopup: true,
      cellEditorParams: {
        multiple: false,
        types: [
          "MANUAL AUDIT",
          "MISSING",
          "OVERAGE",
          "OPENED",
          "BROKEN",
          "CRUSHED",
          "WET",
          "HOLDOVER"
        ]
      }
    },
    {
      field: 'carton_resolution', 
      headerName: 'Carton Resolution', 
      width: 200, 
      editable: true,
      cellEditorPopup: true,
      cellRenderer: cartonResolutionFormatting,
      cellEditor: ContactTypeEditorComponent,
      cellEditorParams: {
        multiple: false,
        types: [
          "DELIVERED AS IS",
          "HELD FOR DISPOSITION",
          "RETAPED AND DELIVERED",
          "REPACKAGED AND DELIVERED",
          "DISCARDED DISPOSED"
        ]
      }
    },
    {
      headerName: 'Action', cellRenderer: (params: any) => {
        const button = document.createElement('button');
        const icon = document.createElement('i');
        button.type = 'button';
        button.classList.add('barcode-delete');
        button.setAttribute('aria-label', 'Delete barcode ' + params.data.barcode);
        button.title = 'Delete barcode';

        // Add Font Awesome classes to the icon element
        icon.classList.add('fas', 'fa-trash'); 

        // Append the icon to the button
        button.appendChild(icon);
        button.addEventListener('click', () => {
          this.zone.run(() => {
            this.deleteBarcode(params);
          });
        });
        return button;        
      }, editable: false, filter: false, sortable: false, width: 100,
    },
  ]
}

  numberOfPallets(): string[] {
    let pallets = [];
    console.log(this.pallet.length);
    for(let i = 0; i < this.pallet?.length; i++) {
      let container = i + 1;
      pallets.push(container.toString());
    }
    return pallets;
  }

  defaultColDef = {
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      caseSensitive: false,
    },
    autoHeight: true,
    suppressHeaderMenuButton: true,
    cellClass: 'cell-wrap-text',
    resizable: true,
    editable: false,
  };

  onGridReady(params: any) {
    this.gridApi = params.api;
    this.gridApi.setGridOption('quickFilterText', this.barcodeSearch);
  }

  editShipmentDetails() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '650px'; // You can use pixels or other units
    dialogConfig.height = '650px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Edit Shipment Details',
        modalMessage: {uuid: this.shipment.details.shipment_list_uuid, shipment: this.shipment},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }


  printPallets() {
    const accessToken = localStorage.getItem('access_token');
    let link: string = '';
    link = `/api/work_orders/print_pdfs/newams_print_pallet_label.php?alternate_reference=${this.alternate_reference}&token=${accessToken}`;
    window.open(link, "_blank")
  }

  // print function checks the string passed to determine which document is printed in the new tab
  //printLabel(doc_type: string, uuid: string) {
  //  const accessToken = localStorage.getItem('access_token');
  //  let link: string = '';
  //
  //  if (doc_type === 'packing') {
  //    link = `/api/work_orders/print_pdfs/newams_print_packing_label.php?uuid=${uuid}&token=${accessToken}`;
  //  }
  //  else if (doc_type === 'pallet') {
  //    link = `/api/work_orders/print_pdfs/newams_print_pallet_label.php?uuid=${uuid}&token=${accessToken}`;
  //  }
  //  else if (doc_type === 'bol') {
  //    console.log(this.alternate_reference)
  //    link = `/api/work_orders/print_pdfs/newams_print_bol.php?uuid=${uuid}&token=${accessToken}`;
  //  }
  //  else if (doc_type === 'trailer') {
  //    link = `/api/work_orders/print_pdfs/newams_print_trailer_label.php?uuid=${uuid}&token=${accessToken}`;
  //  }
  //
  //  window.open(link, "_blank")
  //}
  
  // print function checks the string passed to determine which document is printed in the new tab
  printLabel(doc_type: string, uuid: string) {
    let endpoint = '';
  
    switch (doc_type) {
      case 'packing':
        endpoint = `/api/work_orders/print_pdfs/newams_print_packing_label.php`;
        break;
      case 'pallet':
        endpoint = `/api/work_orders/print_pdfs/newams_print_pallet_label.php`;
        break;
      case 'bol':
        endpoint = `/api/work_orders/print_pdfs/newams_print_bol.php`;
        break;
      case 'trailer':
        endpoint = `/api/work_orders/print_pdfs/newams_print_trailer_label.php`;
        break;
      default:
        return;
    }
  
    this.http.get(endpoint, {
      responseType: 'blob', // Expect binary data
      params: { uuid: uuid }
    }).subscribe(blob => {
      const url = window.URL.createObjectURL(blob);
      window.open(url);
      // Optionally revoke the object URL after some time to free memory
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    });    
  }  

  // print function checks the string passed to determine which document is printed in the new tab
  printBOL(uuid: string, alternate_reference: string, shipper_contact_uuid: string) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '405px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Print Bill Of Lading',
        modalMessage: {lading_uuid: uuid, alternate_reference: alternate_reference, shipper_contact_uuid: shipper_contact_uuid},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);    
  }  

  onCellEditingStopped(event: CellEditingStoppedEvent) {

    const editedValue = event.value;
    const column = event.column;
    const rowNode = event.node;

    const product_uuid = rowNode.data.product_uuid
    const return_status = rowNode.data.return_status
    const carton_resolution = rowNode.data.carton_resolution

    const barcode_status_url = `/api/shipment_lists/shipment_details.php?table=shipment_and_receiving&command=UPDATE_BARCODE_STATUS`;
    const carton_resolution_url = `/api/shipment_lists/shipment_details.php?table=shipment_and_receiving&command=PRODUCT_STATUS_UPDATE`;

    const columnName = column.getColId();

    console.log('Edited Value:', editedValue);
    console.log('Row Data:', rowNode.data);
    console.log('Column Name:', columnName)

    // create POST body of cell edited of column, columnName, and value changed, editedValue
    var postData = {
      [columnName]: editedValue,
      product_uuid: product_uuid
    }

    // if an edit is made in the status section of the barcodes subgrid
    if (columnName === 'return_status') {

      postData = {
        [columnName]: editedValue,
        product_uuid: product_uuid,
      }

      console.log('Post Data:', postData);
      console.log('Stringify:', JSON.stringify(postData))

      return this.http.post<any>(barcode_status_url, postData)
        .subscribe(response => {
          console.log('Post Response: ', response)
        })

      // if an edit is made in the carton resolution section of the barcodes grid
    } else if (columnName === 'carton_resolution') {

      postData = {
        [columnName]: editedValue,
        product_uuid: product_uuid,
        return_status: return_status
      }

      console.log('Post Data:', postData);
      console.log('Stringify:', JSON.stringify(postData))

      return this.http.post<any>(carton_resolution_url, postData)
        .subscribe(response => {
          console.log('Post Response: ', response)
        })
    } else {
      return
    }
  }

  addBarcode() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '320px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Add Barcode',
        modalMessage: {pallets: this.pallet, shipment_list_uuid: this.shipment_uuid},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  updateBarcode() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '180px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Update Work Order Barcodes/Pallet',
        modalMessage: {shipment_list_uuid: this.shipment_uuid},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  changeBarcodeStatus() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '10px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Change Barcodes Status',
        modalMessage: {shipment_list_uuid: this.shipment_uuid},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  moveBarcode() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '180px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Move Barcode Onto Pallet',
        modalMessage: '',
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  deleteBarcode(params: any) {
    const dialogConfig = new MatDialogConfig();
    const product_uuid = params.data.product_uuid;
    const barcode = params.data.barcode;

    dialogConfig.width = '10'; // You can use pixels or other units
    dialogConfig.height = '10';
    dialogConfig.data = {
      data: {
        modalTitle: 'Delete Barcode Confirmation',
        modalMessage: {uuid: product_uuid, barcode: barcode},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  addPallet() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '485px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Add Pallet',
        modalMessage: {shipment_list_uuid: this.shipment.details.shipment_list_uuid,
                       trip_list_uuid: this.shipment.details.trip_list_uuid},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  addMultiplePallets() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '585px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Add Multiple Pallets',
        modalMessage: {shipment_list_uuid: this.shipment.details.shipment_list_uuid,
                       trip_list_uuid: this.shipment.details.trip_list_uuid},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  deletePallet(uuid: string) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '255px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Delete Pallet Confirmation',
        modalMessage: {uuid: uuid},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  editPallet(pallet: any) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '420px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Edit Pallet',
        modalMessage: pallet,
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  deleteServiceRate(contact_profile_default_service_uuid: string, service_type: string, service: string) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '320px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Delete Service Rate Confirmation',
        modalMessage: {uuid: contact_profile_default_service_uuid, service_type: service_type, service: service},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  editService(service_type: string, service: string, flat_rate: number, uuid: string) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '400px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Edit Default Services',
        modalMessage: {service_type: service_type, service: service, flat_rate: flat_rate, uuid: uuid},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  addServiceRate() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '348px'; // You can use pixels or other units
    dialogConfig.height = '120px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Add Service Rate',
        modalMessage: this.shipment.consignee['contact_uuid'],
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  // opens a modal to display the shipment list work order wizard
  createEDIFile(lading_uuid: string, alternate_reference: string): void {
    const dialogConfig = new MatDialogConfig();     

    dialogConfig.width = '10'; // You can use pixels or other units
    dialogConfig.height = '10';
    dialogConfig.data = {
      data: {
        modalTitle: 'Create EDI File',
        modalMessage: {lading_uuid: lading_uuid, alternate_reference: alternate_reference},
      },
    };

    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

}

function shipmentStatusFormatting(params: ICellRendererParams) {
  let shipmentStatuses: string[] = []
  if (params.value != null) {
    shipmentStatuses = params.value.split(",");
  }
  let buttonsHtml = '';

  for (let type of shipmentStatuses) {
    var trimmedType = type.trim();
    let bgColor = '';

    switch (trimmedType) {
      case 'MANUAL AUDIT':
        bgColor = '#52da6f';
        break;
      case 'MISSING':
        bgColor = '#Ff98a4';
        break;
      case 'OVERAGE':
        bgColor = '#D3e426';
        break;
      case 'OPENED':
        bgColor = '#2ab546';
        break;
      case 'BROKEN':
        bgColor = '#87a3d4';
        break;
      case 'CRUSHED':
        bgColor = '#Dad9be';
        break;
      case 'WET':
        bgColor = '#A68c69';
        break;
      case '':
        bgColor = '#Ecb44e';
        break;
      case 'HOLDOVER':
        bgColor = '#9df5b8';
        break;
      default:
        bgColor = '#FFFFFF';
    }

    buttonsHtml += '<button style="background-color:' + bgColor + ';border-radius:10px;font-weight:bold;">' + trimmedType + '</button> <br>';
  }

  let element = '<div>' + buttonsHtml + '</div>';
  if (params.value) {
    return element;
  }
  else {
    return '<div></div>';
  }
}

function cartonResolutionFormatting(params: ICellRendererParams) {
  let cartonResolution: string[] = []
  if (params.value != null) {
    cartonResolution = params.value.split(",");
  }
  let buttonsHtml = '';

  for (let type of cartonResolution) {
    var trimmedType = type.trim();
    let bgColor = '';

    switch (trimmedType) {
      case 'DELIVERED AS IS':
        bgColor = '#52da6f';
        break;
      case 'HELD FOR DISPOSITION':
        bgColor = '#Ff98a4';
        break;
      case 'RETAPED AND DELIVERED':
        bgColor = '#D3e426';
        break;
      case 'REPACKAGED AND DELIVERED':
        bgColor = '#2ab546';
        break;
      case 'DISCARDED DISPOSED':
        bgColor = '#87a3d4';
        break;
      default:
        bgColor = '#FFFFFF';
    }

    buttonsHtml += '<button style="background-color:' + bgColor + ';border-radius:10px;font-weight:bold;">' + trimmedType + '</button> <br>';
  }

  let element = '<div>' + buttonsHtml + '</div>';
  if (params.value) {
    return element;
  }
  else {
    return '<div></div>';
  }
}
